import { StyleSheet, View } from "react-native";
import { LineChart as Wagmi } from "react-native-wagmi-charts";
import { curveLinear } from "d3-shape";

import { pictureScaleOf } from "../pictureScale";
import type { UiTheme } from "../theme";
import type { ChartVariant } from "./chartAnnotations";
import { lineColor, lineDashArray, lineStroke, lineWidth, type LineStroke } from "./chartDots";
import { WAGMI_BOTTOM_RESERVED } from "./wagmiChartModel";

// The `<Wagmi.Provider>` tree of `WagmiLineChart.tsx`: one `<Wagmi>` per
// series, stacked at the same origin, each clipped to `height` so the 40px
// the library reserves under every mount never shows (that file's header).
// It draws only: a tap reads the chart (`ChartTapLayer.tsx`), and no wagmi
// gesture is mounted, so a drag that starts on a chart scrolls the page.
//
// `layers`' element type is narrower than `wagmiChartModel.ts`'s `Layer`:
// this component reads only `id`, `variant` and `stroke`.
type PathLayer = { id: string; variant: ChartVariant; stroke?: LineStroke };

export function WagmiPathLayers({
  data,
  x0,
  xEnd,
  lo,
  hi,
  layers,
  theme,
  width,
  inset = 0,
  height,
  yGutter,
}: {
  data: Record<string, { timestamp: number; value: number }[]>;
  x0: number;
  xEnd: number;
  lo: number;
  hi: number;
  layers: PathLayer[];
  theme: UiTheme;
  width: number;
  /** A picture's margin left of the plot (`WagmiLineChart`). */
  inset?: number;
  height: number;
  /** Same value `wagmiChartModel.ts`'s `pxY` uses (`WAGMI_Y_GUTTER`). */
  yGutter: number;
}) {
  const k = pictureScaleOf(theme);
  return (
    <Wagmi.Provider data={data} xDomain={[x0, xEnd]} yRange={{ min: lo, max: hi }} xLength={1}>
      {layers.map((layer, i) => (
        <View
          key={layer.id}
          style={[
            i > 0 ? layerStyles.absolute : null,
            { width, height, overflow: "hidden", marginLeft: inset },
          ]}
        >
          <Wagmi
            id={layer.id}
            width={width}
            height={height + WAGMI_BOTTOM_RESERVED}
            yGutter={yGutter}
            shape={curveLinear}
            style={{ width, height: height + WAGMI_BOTTOM_RESERVED }}
          >
            <Wagmi.Path
              color={lineColor(theme, layer.variant)}
              width={lineWidth(layer.variant, k)}
              pathProps={pathPropsOf(layer, k)}
            />
          </Wagmi>
        </View>
      ))}
    </Wagmi.Provider>
  );
}

function pathPropsOf(layer: PathLayer, k: number) {
  const dash = lineDashArray(lineStroke(layer.variant, layer.stroke), lineWidth(layer.variant, k), k);
  return dash ? { strokeDasharray: dash, strokeLinecap: "round" as const } : { strokeLinecap: "round" as const };
}

// Each layer is wrapped in its own View so the layers stack at one origin.
const layerStyles = StyleSheet.create({
  absolute: { position: "absolute", top: 0, left: 0 },
});
